// src/components/shared/Navbar.jsx

import ApartmentIcon from "@mui/icons-material/Apartment";
import DescriptionIcon from "@mui/icons-material/Description";
import GridViewIcon from "@mui/icons-material/GridView";
import LogoutIcon from "@mui/icons-material/Logout";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import PublicIcon from "@mui/icons-material/Public";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Box,
  Switch,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  InputAdornment,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "universal-cookie";
import { useMutation } from "@tanstack/react-query";
import { ROUTE_PATHS } from "../../routes/routePath";
import { PowerSearch } from "../pages/powerSearch/PowerSearch";
import { putWithQueryApiServices, getApiServices, getWithQueryApiServices, postWithQueryApiServices } from "../../apis/api";
import { apiRoutes } from "../../apis/apiPath";
import { successToast, errorToast } from "../../utils/helper";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { usePermissions } from "../../hooks/usePermissions";
import { longestCompanySelectWidth } from "./CompanySelect";
import { useDispatch, useSelector } from "react-redux";
import { resetTourState, setTourData, stopTour } from "../../redux/MainReducer";
import { appApi } from "../../apis/config";

// Theme color
const PRIMARY_COLOR = "#7285b3";
const DISABLED_COLOR = "#b0b0b0";

// =====================================================
// NAV ITEMS CONFIGURATION
// =====================================================

const TenantRequiredItems = [
  {
    Icon: GridViewIcon,
    label: "Categories",
    path: ROUTE_PATHS.CATEGORY_LIST,
    requiresTenant: true,
  },
  {
    Icon: ApartmentIcon,
    label: "Company",
    path: ROUTE_PATHS.COMPANY_LIST,
    requiresTenant: true,
  },
  {
    Icon: AdminPanelSettingsIcon,
    label: "Security Profile",
    path: ROUTE_PATHS.SECURITY_LIST,
    requiresTenant: true,
  },
  {
    Icon: PeopleOutlineIcon,
    label: "User",
    path: ROUTE_PATHS.ADD_USERS,
    requiresTenant: true,
  },
  {
    Icon: QrCodeScannerIcon,
    label: "Scan",
    requiresTenant: false,
  },
  {
    Icon: DescriptionIcon,
    label: "Reporting",
    path: ROUTE_PATHS.REPORTING,
    requiresTenant: true,
  },
  {
    Icon: ReceiptLongIcon,
    label: "Billing",
    path: ROUTE_PATHS.INVOICE_LIST,
    requiresTenant: true,
  },
];

const GlobalItems = [
  {
    Icon: CardMembershipIcon,
    label: "Subscription",
    path: ROUTE_PATHS.SUBSCRIPTION_LIST,
    requiresTenant: false,
  },
];

// =====================================================
// NAVBAR COMPONENT
// =====================================================
function Navbar({
  setCurrentCompany,
  currentCompany,
  companyList,
  currentTenant,
  setCurrentTenant,
  tenantList,
  globalCompanyRefetch,
  globalTenantRefetch,
  minimal = false,
  title = "",
  isSearchModalOpen = false,
  onCloseSearchModal,
  onOpenSearchModal,
  billingBadge = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const showHeaderCompanySelect =
    !minimal &&
    (location.pathname === ROUTE_PATHS.HOME ||
      location.pathname === ROUTE_PATHS.REPORTING ||
      location.pathname === `/${ROUTE_PATHS.SEARCH_RESULT}`);
  const cookies = new Cookies();
  const dispatch = useDispatch();
  const isTourEnabled = useSelector((state) => state.mainReducer?.isTourEnabled);
  const tenantID = useSelector((state) => state.mainReducer?.tenantID || state.mainReducer?.tenantId) || cookies.get("defaulttenant") || cookies.get("tenantId");
  const { hasPermission, isGlobalSuperAdmin } = usePermissions(currentCompany);

  // UI State
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const open = Boolean(anchorEl);

  React.useEffect(() => {
    window.reactTourHelpers = window.reactTourHelpers || {};
    window.reactTourHelpers.openMobileDrawer = () => setDrawerOpen(true);
    window.reactTourHelpers.closeMobileDrawer = () => setDrawerOpen(false);
    return () => {
      if (window.reactTourHelpers) {
        delete window.reactTourHelpers.openMobileDrawer;
        delete window.reactTourHelpers.closeMobileDrawer;
      }
    };
  }, []);

  // =====================================================
  // DETERMINE USER TYPE
  // =====================================================
  const isSuperAdmin = !!tenantList;

  // =====================================================
  // FILTER ACTIVE COMPANIES
  // =====================================================
  const companies = React.useMemo(() => {
    return (companyList || []).filter((company) => Number(company.dst) !== 0);
  }, [companyList]);

  const headerCompanySelectWidth = React.useMemo(
    () =>
      longestCompanySelectWidth(
        companies.map((company) => company.name),
        { max: 640, min: 240 },
      ),
    [companies],
  );

  // =====================================================
  // GET CURRENT COMPANY DATA
  // =====================================================
  const currentCompanyData = React.useMemo(() => {
    if (!currentCompany || companies.length === 0) return null;
    const companyID =
      typeof currentCompany === "string"
        ? parseInt(currentCompany, 10)
        : currentCompany;
    return companies.find((c) => c.companyID === companyID);
  }, [currentCompany, companies]);

  // =====================================================
  // CHECK IF SELECTIONS ARE VALID
  // =====================================================
  const isTenantSelected = !!currentTenant && currentTenant !== "";
  const isCompanySelected =
    !!currentCompany && currentCompany !== "" && currentCompany !== 0;

  // =====================================================
  // TENANT HANDLING (FOR SUPER ADMIN)
  // =====================================================
  const tenants = React.useMemo(() => {
    if (!tenantList) return [];
    return tenantList.map((item) => ({
      ...item,
      name: item.tenantName,
    }));
  }, [tenantList]);

  const currentTenantData = tenants?.find((t) => t.dbName === currentTenant);
  const currentTenantEnabled =
    currentTenantData?.tenantEnabled === 1 ||
    currentTenantData?.tenantEnabled === undefined;

  // =====================================================
  // ✅ CALCULATE TENANT STRING FOR API CALLS
  // =====================================================
  const tenantString = React.useMemo(() => {
    if (isSuperAdmin && currentTenant) {
      return `&defaulttenant=${currentTenant}`;
    }
    return "";
  }, [isSuperAdmin, currentTenant]);

  const toggleTenantMutation = useMutation({
    mutationKey: ["toggleTenantEnabled"],
    mutationFn: ({ tenantID, tenantEnabled }) =>
      putWithQueryApiServices(apiRoutes.TOGGLE_TENANT_ENABLED, "", {
        tenantID,
        tenantEnabled,
      }),
    onSuccess: () => {
      successToast(
        `Tenant ${!currentTenantEnabled ? "enabled" : "disabled"} successfully`,
      );
      globalTenantRefetch && globalTenantRefetch();
    },
    onError: (error) => {
      errorToast(
        error.response?.data?.message || "Failed to toggle tenant status",
      );
    },
  });

  const handleTenantToggle = () => {
    if (currentTenantData) {
      toggleTenantMutation.mutate({
        tenantID: currentTenantData.tenantID,
        tenantEnabled: !currentTenantEnabled,
      });
    }
  };

  // =====================================================
  // EVENT HANDLERS
  // =====================================================

  const handleTenantChange = (e) => {
    const value = e.target.value;
    setCurrentTenant(value);
  };

  const handleHeaderCompanyChange = (e) => {
    if (setCurrentCompany) setCurrentCompany(e.target.value);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  // =====================================================
  // SCAN HANDLER
  // =====================================================
  const handleScanClick = async () => {
    try {
      console.log("🚀 SCAN BUTTON CLICKED");
      const queryParam = currentTenant ? `defaulttenant=${currentTenant}` : "";
      const response = await getWithQueryApiServices(apiRoutes.GET_SCAN_DATA, queryParam);

      if (response?.data?.sessionhash) {
        const pdisUrl = `pdis://${response.data.sessionhash}`;
        window.location.href = pdisUrl;
        successToast(`Opening scanning application...`);
      } else {
        errorToast("Failed to get session identifier");
      }
    } catch (error) {
      console.error("❌ Scan Error:", error);
      errorToast(error?.response?.data?.message || "Failed to get scan data");
    }
  };

  // =====================================================
  // TOUR PREFERENCE HANDLER
  // =====================================================
  const handleTourToggle = async (event) => {
    const checked = event.target.checked;
    handleClose(); // Close desktop profile menu dropdown
    setDrawerOpen(false); // Close mobile drawer
    try {
      const queryParam = tenantID ? `defaulttenant=${tenantID}` : "";
      if (checked) {
        // Toggle ON: Reset tours on backend and enable in Redux
        await postWithQueryApiServices(apiRoutes.RESET_TOURS, queryParam);
        dispatch(setTourData({ completedTours: {}, isTourEnabled: true }));
      } else {
        // Toggle OFF: Disable tours
        await postWithQueryApiServices(
          apiRoutes.COMPLETE_TOUR,
          queryParam,
          { tourKey: "all", completed: true }
        );
        dispatch(setTourData({ completedTours: {}, isTourEnabled: false }));
        dispatch(stopTour()); // Stop any active tour
      }
    } catch (err) {
      console.error("[Navbar] Failed to toggle tour preference:", err);
      errorToast("Failed to update tour preference");
    }
  };

  // =====================================================
  // LOGOUT HANDLER
  // =====================================================
  const logout = () => {
    const wasSuperAdmin = isSuperAdmin;

    // Reset tour status in Redux
    dispatch(resetTourState());

    // Clear all cookies (except tenantName)
    const allCookies = cookies.getAll();
    Object.keys(allCookies).forEach((cookieName) => {
      if (cookieName !== "tenantName") {
        cookies.remove(cookieName, { path: "/" });
      }
    });

    if (wasSuperAdmin) {
      navigate(ROUTE_PATHS.ADMIN_LOGIN || "/admin/login");
    } else {
      navigate(ROUTE_PATHS.LOGIN || "/");
    }
  };

  // =====================================================
  // PROFILE MENU ACTIONS & STATE
  // =====================================================
  // Change Password State
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmNewPassword, setConfirmNewPassword] = React.useState("");
  const [changePasswordError, setChangePasswordError] = React.useState("");

  // Password Visibility States
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = React.useState(false);

  // IP State
  const [ipDialogOpen, setIpDialogOpen] = React.useState(false);
  const [ipDetails, setIpDetails] = React.useState(null);
  const [loadingIp, setLoadingIp] = React.useState(false);

  // Terms of Service Editor State
  const [termsEditorOpen, setTermsEditorOpen] = React.useState(false);
  const [termsText, setTermsText] = React.useState("");
  const [termsLoading, setTermsLoading] = React.useState(false);

  const handleSaveTerms = async () => {
    try {
      setTermsLoading(true);
      const res = await appApi.put("terms-of-service", { terms: termsText });
      if (res.data?.success) {
        successToast("Terms of Service updated successfully!");
        setTermsEditorOpen(false);
      } else {
        errorToast(res.data?.message || "Failed to update terms.");
      }
    } catch (err) {
      console.error("Failed to update terms:", err);
      errorToast(err.response?.data?.message || "Error saving Terms of Service");
    } finally {
      setTermsLoading(false);
    }
  };

  const changePasswordMutation = useMutation({
    mutationFn: (data) =>
      putWithQueryApiServices(
        apiRoutes.CHANGE_PASSWORD,
        `selectedCompany=${currentCompany || ""}${tenantString}`,
        data
      ),
    onSuccess: () => {
      successToast("Password updated successfully!");
      setChangePasswordOpen(false);
      // Reset
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setChangePasswordError("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmNewPassword(false);
    },
    onError: (err) => {
      const msg = err?.response?.data?.error || err?.message || "Failed to update password";
      setChangePasswordError(msg);
    }
  });

  const fetchIpDetails = async (showToastOnError = false) => {
    try {
      setLoadingIp(true);
      const queryParam = `selectedCompany=${currentCompany || ""}${tenantString}`;
      const response = await getWithQueryApiServices(apiRoutes.GET_MY_IP, queryParam);
      setIpDetails(response?.data);
    } catch (error) {
      console.error("Failed to fetch IP details:", error);
      if (showToastOnError) {
        errorToast("Failed to fetch IP details");
      }
    } finally {
      setLoadingIp(false);
    }
  };

  React.useEffect(() => {
    if (!minimal) {
      fetchIpDetails(false);
    }
  }, [currentCompany, tenantString, minimal]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setChangePasswordError("All fields are required");
      return;
    }
    if (newPassword.length < 6) {
      setChangePasswordError("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setChangePasswordError("New passwords do not match");
      return;
    }
    setChangePasswordError("");
    changePasswordMutation.mutate({ currentPassword, newPassword });
  };

  // =====================================================
  // USER INFO
  // =====================================================
  const allCookies = cookies.getAll();
  const userFullName = allCookies?.userFullName || "";
  const userEmail = allCookies?.userEmail || allCookies?.title || "";

  const getInitialsFromName = (name = "") => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (
      parts[0].charAt(0).toUpperCase() +
      parts[parts.length - 1].charAt(0).toUpperCase()
    );
  };

  // =====================================================
  // NAV ITEMS
  // =====================================================
  const navbarItems = React.useMemo(() => {
    if (minimal) return [];
    
    const items = isSuperAdmin 
      ? [...TenantRequiredItems, ...GlobalItems]
      : [...TenantRequiredItems];

    // Filter items based on granular permissions
    return items.filter(item => {
      if (isGlobalSuperAdmin) return true; // ONLY Global Super Admins see everything
      
      // Map labels to permission strings
      switch (item.label) {
        case "Categories": return hasPermission("Categories: Read");
        case "Company": return hasPermission("Companies: Read");
        case "Security Profile": return hasPermission("Security: Read");
        case "User": return hasPermission("Users: Read") || hasPermission("User: Read");
        case "Reporting": return hasPermission("Reporting: Read");
        case "Billing": return hasPermission("Invoices: Read");
        case "Scan": return hasPermission("Scan: Access"); 
        case "Subscription": return isGlobalSuperAdmin; // Strictly Global Super Admin only
        default: return false; // Default to hidden for safety
      }
    });
  }, [minimal, isSuperAdmin, hasPermission]);

  const billingDotColor =
    billingBadge === "red"
      ? "#d32f2f"
      : billingBadge === "yellow"
        ? "#f9a825"
        : billingBadge === "green"
          ? "#2e7d32"
          : null;

  const BillingBadgeDot = ({ forLabel }) =>
    forLabel === "Billing" && billingDotColor ? (
      <Box
        sx={{
          position: "absolute",
          top: 2,
          right: 2,
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: billingDotColor,
          border: "1.5px solid #fff",
          boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
        }}
      />
    ) : null;

  const isItemDisabled = (item) => {
    if (isSuperAdmin) {
      return item.requiresTenant && !isTenantSelected;
    }
    return item.requiresTenant && !isCompanySelected;
  };

  const getTooltipMessage = (item) => {
    if (isItemDisabled(item)) {
      return isSuperAdmin
        ? "Please select a tenant first"
        : "Please select a company first";
    }
    return item.label;
  };

  const handleNavItemClick = (item) => {
    if (item.label === "Scan") {
      handleScanClick();
    } else {
      if (item.path) {
        navigate(item.path);
      }
      onCloseSearchModal && onCloseSearchModal();
    }
    setDrawerOpen(false);
  };

  // =====================================================
  // MOBILE DRAWER CONTENT
  // =====================================================
  const drawerContent = (
    <Box sx={{ width: 280 }} role="presentation">
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 600 }}>
          Menu
        </Typography>
        <IconButton onClick={toggleDrawer(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Company Info (for tenant admin) */}
      {!isSuperAdmin && currentCompanyData && (
        <Box sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
          <Typography variant="caption" color="textSecondary">
            Current Company
          </Typography>
          <Tooltip title={currentCompanyData.name} arrow placement="bottom-start">
            <Typography variant="body2" fontWeight={600} noWrap>
              {currentCompanyData.name}
            </Typography>
          </Tooltip>
        </Box>
      )}

      {/* Nav Items */}
      <List>
        {navbarItems.map((item, index) => {
          const disabled = isItemDisabled(item);
          const { Icon, label } = item;

          return (
            <ListItem key={index} disablePadding>
              <ListItemButton
                id={`tour-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-mobile`}
                disabled={disabled}
                onClick={() => !disabled && handleNavItemClick(item)}
                sx={{
                  opacity: disabled ? 0.5 : 1,
                  "&.Mui-disabled": { opacity: 0.5 },
                }}
              >
                <ListItemIcon sx={{ position: "relative", minWidth: 40 }}>
                  <Icon
                    sx={{ color: disabled ? DISABLED_COLOR : "#4b4b4c" }}
                  />
                  <BillingBadgeDot forLabel={label} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  secondary={
                    disabled
                      ? isSuperAdmin
                        ? "Select tenant first"
                        : "Select company first"
                      : ""
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      <Box id="tour-nav-profile-mobile">
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setDrawerOpen(false);
                setChangePasswordOpen(true);
              }}
            >
              <ListItemIcon>
                <LockIcon sx={{ color: "#4b4b4c" }} />
              </ListItemIcon>
              <ListItemText primary="Change Password" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setDrawerOpen(false);
                setIpDialogOpen(true);
                fetchIpDetails(true);
              }}
            >
              <ListItemIcon>
                <SettingsEthernetIcon sx={{ color: "#4b4b4c" }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                    <Typography variant="body1">IP</Typography>
                    {ipDetails?.ip ? (
                      <Typography variant="body2" sx={{ fontFamily: "monospace", color: "text.secondary", ml: 1 }}>
                        ({ipDetails.ip})
                      </Typography>
                    ) : loadingIp ? (
                      <CircularProgress size={14} />
                    ) : null}
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>

          {!isGlobalSuperAdmin && (
            <ListItem disablePadding>
              <ListItemButton
                disableRipple
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  cursor: "default",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ListItemIcon>
                    <HelpOutlineIcon sx={{ color: "#4b4b4c" }} />
                  </ListItemIcon>
                  <ListItemText primary="Tour Preference" />
                </Box>
                <Switch
                  checked={isTourEnabled}
                  onChange={handleTourToggle}
                  size="small"
                />
              </ListItemButton>
            </ListItem>
          )}
        </List>

        <Divider />

        {/* Logout */}
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={logout}>
              <ListItemIcon>
                <LogoutIcon sx={{ color: "#d32f2f" }} />
              </ListItemIcon>
              <ListItemText primary="Logout" sx={{ color: "#d32f2f" }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <>
      <AppBar
        position="relative"
        color="transparent"
        elevation={0}
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "white",
          zIndex: 1100,
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 1, lg: 2 } }}>
          <Toolbar disableGutters sx={{ gap: 1, py: 1, minWidth: 0, width: "100%" }}>
            {/* ===== MOBILE MENU ICON ===== */}
            {!minimal && (
              <IconButton
                sx={{ display: { xs: "flex", lg: "none" }, mr: 1 }}
                onClick={toggleDrawer(true)}
              >
                <MenuIcon sx={{ color: PRIMARY_COLOR }} />
              </IconButton>
            )}

            {/* ===== LOGO ===== */}
            <PublicIcon
              sx={{
                color: PRIMARY_COLOR,
                fontSize: { xs: 40, sm: 50 },
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={() => {
                if (minimal) return;
                navigate(ROUTE_PATHS.HOME);
              }}
            />

            {minimal && title && (
              <Typography
                variant="h6"
                sx={{
                  color: PRIMARY_COLOR,
                  fontWeight: 600,
                  display: { xs: "none", sm: "block" },
                  ml: 2,
                }}
              >
                {title}
              </Typography>
            )}

            {showHeaderCompanySelect && (
              <FormControl
                sx={{
                  width: { xs: "100%", sm: headerCompanySelectWidth },
                  minWidth: { xs: 160, sm: headerCompanySelectWidth },
                  flexShrink: 0,
                }}
                size="small"
              >
                <InputLabel id="header-company-select-label" shrink>
                  Company
                </InputLabel>
                <Select
                  labelId="header-company-select-label"
                  value={currentCompanyData ? currentCompanyData.companyID : ""}
                  label="Company"
                  onChange={handleHeaderCompanyChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#999", fontSize: "14px" }}>
                          Select
                        </span>
                      );
                    }
                    const selectedCompany = companies.find(
                      (company) => String(company.companyID) === String(selected),
                    );
                    return selectedCompany?.name || "";
                  }}
                >
                  <MenuItem value="">
                    <em>Select Company</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem
                      key={company.companyID}
                      value={company.companyID}
                    >
                      <Typography sx={{ whiteSpace: "normal" }}>
                        {company.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* ===== SUPER ADMIN: TENANT DROPDOWN ===== */}
            {!minimal && isSuperAdmin && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                  minWidth: 0,
                }}
              >
                <FormControl
                  id="tour-company-select"
                  sx={{ width: { xs: 110, sm: 140, lg: 160 }, minWidth: 110 }}
                  size="small"
                >
                  <InputLabel id="tenant-select-label" shrink>
                    Tenant
                  </InputLabel>
                  <Select
                    labelId="tenant-select-label"
                    value={currentTenant || ""}
                    label="Tenant"
                    onChange={handleTenantChange}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#999", fontSize: "14px" }}>
                            Select
                          </span>
                        );
                      }
                      const selectedTenant = tenants?.find(
                        (t) => t.dbName === selected,
                      );
                      return selectedTenant?.tenantName || "";
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Tenant</em>
                    </MenuItem>
                    {tenants?.map((tenant) => (
                      <MenuItem
                        key={tenant.dbName}
                        value={tenant.dbName}
                        sx={{ maxWidth: 300 }}
                      >
                        <Tooltip title={tenant.tenantName} arrow placement="right">
                          <Typography noWrap sx={{ width: "100%" }}>
                            {tenant.tenantName}
                          </Typography>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {isTenantSelected && currentTenantData && (
                  <Tooltip
                    title={
                      currentTenantEnabled
                        ? "Tenant is active - click to disable"
                        : "Tenant is disabled - click to enable"
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: currentTenantEnabled
                            ? "#2e7d32"
                            : "#d32f2f",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          display: { xs: "none", sm: "block" },
                        }}
                      >
                        {currentTenantEnabled ? "Active" : "Inactive"}
                      </Typography>

                      <Switch
                        checked={currentTenantEnabled}
                        onChange={handleTenantToggle}
                        disabled={toggleTenantMutation.isPending}
                        size="small"
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}

            {/* ===== POWER SEARCH - CONDITIONALLY RENDER ===== */}
            {!isSearchModalOpen && (hasPermission("Search: Access") || isGlobalSuperAdmin) && (
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  flex: "1 1 148px",
                  width: "auto",
                  maxWidth: 420,
                  minWidth: 148,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <PowerSearch
                    currentCompany={currentCompany}
                    tenantString={tenantString}
                  />
                </Box>
              </Box>
            )}

            {/* ===== SPACER ===== */}
            <Box sx={{ flexGrow: 1 }} />

            {/* ===== DESKTOP NAV ITEMS ===== */}
            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                gap: 0.25,
                flexShrink: 0,
              }}
            >
              {navbarItems?.map((item, index) => {
                const disabled = isItemDisabled(item);
                const iconColor = disabled ? DISABLED_COLOR : "#4b4b4c";
                const tooltipMessage = getTooltipMessage(item);

                return (
                  <Tooltip key={index} title={tooltipMessage} arrow>
                    <span>
                      <IconButton
                        id={`tour-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        disabled={disabled}
                        onClick={() => handleNavItemClick(item)}
                        disableRipple
                        sx={{
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                          "&.Mui-disabled": {
                            cursor: "not-allowed",
                            pointerEvents: "auto",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            padding: "0 4px",
                            opacity: disabled ? 0.5 : 1,
                            transition: "opacity 0.2s",
                            position: "relative",
                          }}
                        >
                          <Box sx={{ position: "relative", lineHeight: 0 }}>
                            <item.Icon style={{ color: iconColor }} />
                            <BillingBadgeDot forLabel={item.label} />
                          </Box>
                          <Typography sx={{ fontSize: 12, color: iconColor }}>
                            {item.label}
                          </Typography>
                        </Box>
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              })}
            </Box>

            <Box
              sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", flexShrink: 0 }}
            >
              {/* ===== USER AVATAR ===== */}
              <IconButton id="tour-nav-profile" sx={{ p: 0, ml: 1 }} onClick={handleClick}>
                <Avatar
                  title={userFullName}
                  sx={{
                    bgcolor: PRIMARY_COLOR,
                    width: { sm: 40 },
                    height: { sm: 40 },
                  }}
                >
                  {getInitialsFromName(userFullName)}
                </Avatar>
              </IconButton>

              {/* ===== USER MENU ===== */}
              <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{ "aria-labelledby": "basic-button" }}
                PaperProps={{ sx: { minWidth: 220 } }}
              >
                <MenuItem
                  onClick={() => {
                    handleClose();
                    setChangePasswordOpen(true);
                  }}
                  sx={{
                    color: "#4b4b4c",
                  }}
                >
                  <ListItemIcon>
                    <LockIcon fontSize="small" sx={{ color: "#4b4b4c" }} />
                  </ListItemIcon>
                  Change Password
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    setIpDialogOpen(true);
                    fetchIpDetails(true);
                  }}
                  sx={{
                    color: "#4b4b4c",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ListItemIcon>
                      <SettingsEthernetIcon fontSize="small" sx={{ color: "#4b4b4c" }} />
                    </ListItemIcon>
                    IP
                  </Box>
                  {ipDetails?.ip ? (
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: "text.secondary", ml: 2 }}>
                      {ipDetails.ip}
                    </Typography>
                  ) : loadingIp ? (
                    <CircularProgress size={14} sx={{ ml: 2 }} />
                  ) : null}
                </MenuItem>
                {!isGlobalSuperAdmin && (
                  <MenuItem
                    sx={{
                      color: "#4b4b4c",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      py: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ListItemIcon>
                        <HelpOutlineIcon fontSize="small" sx={{ color: "#4b4b4c" }} />
                      </ListItemIcon>
                      <Typography sx={{ color: "#4b4b4c", fontSize: "14px" }}>Tour Preference</Typography>
                    </Box>
                    <Switch
                      checked={isTourEnabled}
                      onChange={handleTourToggle}
                      size="small"
                    />
                  </MenuItem>
                )}
                {isGlobalSuperAdmin && (
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      // Fetch current terms
                      setTermsLoading(true);
                      setTermsEditorOpen(true);
                      appApi.get("terms-of-service")
                        .then(res => {
                          if (res.data?.success) {
                            setTermsText(res.data.terms);
                          }
                        })
                        .catch(err => console.error("Error loading terms", err))
                        .finally(() => setTermsLoading(false));
                    }}
                    sx={{ color: "#4b4b4c" }}
                  >
                    <ListItemIcon>
                      <DescriptionIcon fontSize="small" sx={{ color: "#4b4b4c" }} />
                    </ListItemIcon>
                    Edit Terms of Service
                  </MenuItem>
                )}
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  onClick={logout}
                  sx={{
                    color: "#d32f2f",
                    mt: 0,
                    "&:hover": { backgroundColor: "#ffebee" },
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ===== MOBILE DRAWER ===== */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{ zIndex: 1400 }}
      >
        {drawerContent}
      </Drawer>

      {/* ===== CHANGE PASSWORD DIALOG ===== */}
      <Dialog
        open={changePasswordOpen}
        disableEnforceFocus={true}
        onClose={() => {
          if (!changePasswordMutation.isPending) {
            setChangePasswordOpen(false);
            setChangePasswordError("");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmNewPassword("");
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            setShowConfirmNewPassword(false);
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1,
            minWidth: { xs: "90%", sm: 400 },
            boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.08)",
          }
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <LockIcon sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="h6" fontWeight={600} color="#333">
            Change Password
          </Typography>
        </DialogTitle>
        <form onSubmit={handlePasswordSubmit}>
          <DialogContent sx={{ pt: 1, pb: 2 }}>
            {changePasswordError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {changePasswordError}
              </Alert>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Current Password"
                type={showCurrentPassword ? "text" : "password"}
                fullWidth
                size="small"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                disabled={changePasswordMutation.isPending}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                        size="small"
                        tabIndex={-1}
                      >
                        {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                fullWidth
                size="small"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={changePasswordMutation.isPending}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        size="small"
                        tabIndex={-1}
                      >
                        {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                label="Confirm New Password"
                type={showConfirmNewPassword ? "text" : "password"}
                fullWidth
                size="small"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                disabled={changePasswordMutation.isPending}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                        edge="end"
                        size="small"
                        tabIndex={-1}
                      >
                        {showConfirmNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setChangePasswordOpen(false);
                setChangePasswordError("");
                setCurrentPassword("");
                setNewPassword("");
                setConfirmNewPassword("");
                setShowCurrentPassword(false);
                setShowNewPassword(false);
                setShowConfirmNewPassword(false);
              }}
              color="inherit"
              disabled={changePasswordMutation.isPending}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={changePasswordMutation.isPending}
              sx={{
                textTransform: "none",
                backgroundColor: PRIMARY_COLOR,
                "&:hover": { backgroundColor: "#5d6d96" },
              }}
            >
              {changePasswordMutation.isPending ? "Updating..." : "Update Password"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ===== IP DIALOG ===== */}
      <Dialog
        open={ipDialogOpen}
        disableEnforceFocus={true}
        onClose={() => setIpDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1,
            minWidth: { xs: "90%", sm: 450 },
            boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.08)",
          }
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <SettingsEthernetIcon sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="h6" fontWeight={600} color="#333">
            IP
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          {loadingIp ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} sx={{ color: PRIMARY_COLOR }} />
            </Box>
          ) : ipDetails ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 2, border: "1px solid #eef0f2" }}>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  Your Detected Public IP
                </Typography>
                <Typography variant="h5" sx={{ fontFamily: "monospace", fontWeight: 700, color: PRIMARY_COLOR }}>
                  {ipDetails.ip}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: ipDetails.allowAnywhere ? "#4caf50" : "#1976d2",
                  }}
                />
                <Typography variant="body2" fontWeight={600} color="#333">
                  Status: {ipDetails.allowAnywhere ? "Access Allowed From Anywhere" : `Restricted via Security Profile: ${ipDetails.profileName}`}
                </Typography>
              </Box>

              {!ipDetails.allowAnywhere && ipDetails.ipRules && ipDetails.ipRules.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom color="#555">
                    Allowed IP Configurations
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 150, overflowY: "auto", mt: 1 }}>
                    {ipDetails.ipRules.map((rule, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          bgcolor: "#f1f3f9",
                          borderRadius: 1,
                          borderLeft: "4px solid",
                          borderColor: PRIMARY_COLOR,
                          fontFamily: "monospace",
                          fontSize: 13,
                        }}
                      >
                        {rule.type === "single" && `Single IP: ${rule.ipAddress}`}
                        {rule.type === "range" && `IP Range: ${rule.startIP} - ${rule.endIP}`}
                        {rule.type === "subnet" && `Subnet CIDR: ${rule.subnet}`}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {!ipDetails.allowAnywhere && (!ipDetails.ipRules || ipDetails.ipRules.length === 0) && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  No specific IP whitelist rules found for your security profile. Access might be restricted.
                </Alert>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="error">
              Unable to load IP details.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setIpDialogOpen(false)}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: PRIMARY_COLOR,
              "&:hover": { backgroundColor: "#5d6d96" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== TERMS OF SERVICE EDITOR DIALOG ===== */}
      <Dialog
        open={termsEditorOpen}
        onClose={() => !termsLoading && setTermsEditorOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, padding: 1 }
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <DescriptionIcon sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="h6" fontWeight={600} color="#333">
            Edit Terms of Service
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, minHeight: 400 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Provide formatting using the rich text controls below. The layout will be saved in HTML format automatically.
          </Typography>
          {termsLoading && !termsText ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
          ) : (
            <ReactQuill
              theme="snow"
              value={termsText}
              onChange={setTermsText}
              placeholder="Write Terms of Service details..."
              style={{ height: 320, marginBottom: 50 }}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                  ['clean']
                ],
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTermsEditorOpen(false)} disabled={termsLoading}>Cancel</Button>
          <Button
            onClick={handleSaveTerms}
            variant="contained"
            color="primary"
            disabled={termsLoading}
            sx={{ textTransform: "none", backgroundColor: PRIMARY_COLOR }}
          >
            {termsLoading ? <CircularProgress size={20} /> : "Save Terms"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Navbar;
